import { predictModularAccountV2Address, toModularAccountV2 } from '@alchemy/smart-accounts';
import { SELECTED_ACCOUNT_CHAIN_ID, addressSchema, bytes32Schema, type Address, type SignerAdapter, type VaultIdentity } from '@hoodmorning/contracts';
import { encodeFunctionData, getAddress, isAddressEqual, toBytes, type Chain, type Client, type Transport } from 'viem';
import { toAccount } from 'viem/accounts';
import { getChainId, getCode, readContract } from 'viem/actions';
import { createSmaBVaultIntent, assertDistinctVault, EXPECTED_SMA_B_ACCOUNT_ID, type VaultAddressIdentity } from './vault.js';
import { semiModularAccountAbi } from './abis.js';
import { z } from 'zod';

export const createAccountAbi = [{ type: 'function', name: 'createSemiModularAccount', stateMutability: 'nonpayable', inputs: [{ name: 'owner', type: 'address' }, { name: 'salt', type: 'uint256' }], outputs: [{ name: 'account', type: 'address' }] }] as const;

// Exact SDK5.2/viem2.56 owner handoff for the entity-0 fallback signer. The SDK
// DeferredAction exception is not an approved Product1 permission path.
const replaySafeSigningSchema = z.strictObject({
  domain: z.strictObject({ chainId: z.literal(SELECTED_ACCOUNT_CHAIN_ID), verifyingContract: addressSchema }),
  types: z.strictObject({
    ReplaySafeHash: z.tuple([z.strictObject({ name: z.literal('hash'), type: z.literal('bytes32') })]),
    EIP712Domain: z.tuple([z.strictObject({ name: z.literal('chainId'), type: z.literal('uint256') }),
      z.strictObject({ name: z.literal('verifyingContract'), type: z.literal('address') })]),
  }),
  primaryType: z.literal('ReplaySafeHash'), message: z.strictObject({ hash: bytes32Schema }),
});

/** Pure installed-SDK prediction; not an on-chain deployment or code-pin check. */
export function predictSmaBVaultAddress<ChainId extends 4663 | 46630>(identity: VaultAddressIdentity<ChainId>) {
  const intent = createSmaBVaultIntent(identity);
  return predictModularAccountV2Address({ factoryAddress: intent.factory, implementationAddress: intent.implementation,
    salt: BigInt(intent.salt), type: 'SMA', ownerAddress: intent.owner });
}

/** Bridge user-controlled signing without importing private keys or exposing transaction signing. */
export async function ownerAccountFromSigner(signer: SignerAdapter) {
  const address = getAddress(await signer.getOwnerAddress());
  const assertCurrentOwner = async () => {
    if (!isAddressEqual(address, await signer.getOwnerAddress())) throw new Error('Controlling signer changed; reconnect');
  };
  return toAccount({
    address,
    async signMessage({ message }) {
      await assertCurrentOwner();
      const bytes = typeof message === 'string' ? toBytes(message)
        : typeof message.raw === 'string' ? toBytes(message.raw) : message.raw;
      return signer.signMessage(bytes);
    },
    async signTypedData(data) {
      await assertCurrentOwner();
      return signer.signTypedData(data);
    },
    async signTransaction() {
      throw new Error('Owner raw transaction signing is not part of the vault user-operation path');
    },
  });
}

/** Constructs the installed SDK account explicitly in factory SMA-B mode.
 * This does not authorize submission: callers still must verify pins, deployment/owner/modules,
 * simulate the exact user operation, enforce spend limits and get user approval.
 */
export async function createOwnerSignedSmaBAccount<ChainId extends 4663 | 46630>(input: {
  client: Client<Transport, Chain, undefined>;
  identity: VaultIdentity<ChainId>;
  signer: SignerAdapter;
  /** After rotation, identity.owner remains the ORIGINAL deployment owner. */
  existingVault?: Address;
}) {
  // Keep original derivation and capability references across signer/RPC awaits.
  const client = input.client, signer = input.signer;
  const identity = structuredClone(input.identity), existingVault = input.existingVault;
  function assertSelectedClient() {
    if (client.chain.id !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('SMA-B client must use the selected Robinhood Chain');
  }
  assertSelectedClient();
  const intent = createSmaBVaultIntent(identity);
  const predicted = predictSmaBVaultAddress(identity);
  const owner = await ownerAccountFromSigner({
    kind: signer.kind,
    getOwnerAddress: () => signer.getOwnerAddress(),
    signMessage: bytes => { assertSelectedClient(); return signer.signMessage(bytes); },
    signTypedData: data => {
      // Check the actual SDK-derived domain AFTER its awaited work/owner check,
      // not just client metadata before/after a potentially changed derivation.
      const saved = structuredClone(data), parsed = replaySafeSigningSchema.safeParse(saved);
      assertSelectedClient();
      if (!parsed.success || !isAddressEqual(getAddress(parsed.data.domain.verifyingContract), predicted)) throw new Error('SDK signing domain mismatch');
      return signer.signTypedData(saved);
    },
  });
  assertSelectedClient();
  if (existingVault !== undefined) {
    if (!isAddressEqual(existingVault, predicted)) throw new Error('Vault differs from original deployment identity');
    if (await getChainId(client) !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('RPC chain mismatch');
    const code = await getCode(client, { address: predicted });
    if (!code || code === '0x') throw new Error('Existing vault is not deployed');
    const accountId = await readContract(client, { address: predicted, abi: semiModularAccountAbi, functionName: 'accountId' });
    if (accountId !== EXPECTED_SMA_B_ACCOUNT_ID) throw new Error('Existing vault profile mismatch');
    const [currentOwner, disabled] = await readContract(client, { address: predicted, abi: semiModularAccountAbi, functionName: 'getFallbackSignerData' });
    if (disabled || !isAddressEqual(currentOwner, owner.address)) throw new Error('Signer is not the active on-chain owner');
  } else if (!isAddressEqual(owner.address, identity.owner)) {
    throw new Error('Signer does not match vault identity');
  }
  const account = await toModularAccountV2({
    client,
    owner,
    mode: 'default',
    signerEntity: { entityId: 0, isGlobalValidation: true },
    factory: intent.factory,
    implementationAddress: intent.implementation,
    accountAddress: predicted,
    // Deployment args never change to the new owner during reconstruction after rotation.
    factoryData: encodeFunctionData({ abi: createAccountAbi, functionName: 'createSemiModularAccount', args: [intent.owner, BigInt(intent.salt)] }),
  });
  assertDistinctVault(owner.address, account.address);
  if (!isAddressEqual(account.entryPoint.address, intent.entryPoint) || account.entryPoint.version !== '0.7')
    throw new Error('SDK EntryPoint does not match approved vault profile');
  assertSelectedClient();
  // SDK5.2 permits caller-selected UO.chainId and reads client.chain for ERC1271
  // wrappers. Bind these low-level signatures to this build before the prompt;
  // full review/simulation/owner/source checks still belong to each feature.
  const sdkSignMessage = account.signMessage.bind(account), sdkSignTypedData = account.signTypedData.bind(account),
    sdkSignUserOperation = account.signUserOperation.bind(account);
  const signMessage: typeof account.signMessage = async args => {
    const saved = structuredClone(args); assertSelectedClient();
    const signature = await sdkSignMessage(saved); assertSelectedClient(); return signature;
  };
  const signTypedData: typeof account.signTypedData = async args => {
    const saved = structuredClone(args); assertSelectedClient();
    const signature = await sdkSignTypedData(saved); assertSelectedClient(); return signature;
  };
  const signUserOperation: typeof account.signUserOperation = async args => {
    const saved = structuredClone(args); assertSelectedClient();
    if (saved.chainId !== undefined && saved.chainId !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('User-operation signing chain mismatch');
    const signature = await sdkSignUserOperation({ ...saved, chainId: SELECTED_ACCOUNT_CHAIN_ID });
    assertSelectedClient(); return signature;
  };
  return { ...account, signMessage, signTypedData, signUserOperation };
}
